/**
 * GameCommentsPanel — displays and allows adding comments on a library game.
 *
 * Shows comments grouped by target (game, trick, card, bid).
 * Used in the training/review mode.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { LibraryComment, CommentTargetType } from '../../types/library';
import { BACKEND_URL } from '../../config';

export interface CommentContext {
  type: CommentTargetType;
  trickNum?: number;
  cardIndex?: number;
  bidIndex?: number;
  label?: string;
}

interface GameCommentsPanelProps {
  pseudo: string;
  gameId: number;
  gameSource: 'own' | 'shared';
  commentsEnabled: boolean;
  currentTrickNum?: number;
  /** Pre-filled commenting context (from right-click etc.) */
  commentContext?: CommentContext | null;
  onClearContext?: () => void;
}

function targetLabel(c: LibraryComment): string | null {
  if (c.target_type === 'trick' && c.target_trick_num != null) return `Pli ${c.target_trick_num}`;
  if (c.target_type === 'card' && c.target_trick_num != null) return `Carte pli ${c.target_trick_num}`;
  if (c.target_type === 'bid' && c.target_trick_num != null) return `Enchère J${c.target_trick_num}`;
  return null;
}

export default function GameCommentsPanel({
  pseudo,
  gameId,
  gameSource,
  commentsEnabled,
  currentTrickNum,
  commentContext,
  onClearContext,
}: GameCommentsPanelProps) {
  const [comments, setComments] = useState<LibraryComment[]>([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [commentTarget, setCommentTarget] = useState<CommentTargetType>('game');
  const [contextTrickNum, setContextTrickNum] = useState<number | undefined>();
  const [contextCardIndex, setContextCardIndex] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Apply incoming context
  useEffect(() => {
    if (commentContext) {
      setCommentTarget(commentContext.type);
      setContextTrickNum(commentContext.trickNum);
      setContextCardIndex(commentContext.cardIndex ?? commentContext.bidIndex);
    }
  }, [commentContext]);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/games/${gameId}/comments?pseudo=${encodeURIComponent(pseudo)}&game_source=${gameSource}`,
      );
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments ?? []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [pseudo, gameId, gameSource]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = useCallback(async () => {
    if (!newCommentText.trim() || submitting) return;
    setSubmitting(true);

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/games/${gameId}/comments?pseudo=${encodeURIComponent(pseudo)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            game_source: gameSource,
            target_type: commentTarget,
            target_trick_num: (commentTarget === 'trick' || commentTarget === 'card' || commentTarget === 'bid')
              ? (contextTrickNum ?? currentTrickNum)
              : undefined,
            target_card_index: (commentTarget === 'card' || commentTarget === 'bid')
              ? contextCardIndex
              : undefined,
            text: newCommentText.trim(),
          }),
        },
      );

      if (res.ok) {
        setNewCommentText('');
        setCommentTarget('game');
        setContextTrickNum(undefined);
        setContextCardIndex(undefined);
        onClearContext?.();
        fetchComments();
      }
    } catch { /* ignore */ }
    setSubmitting(false);
  }, [newCommentText, submitting, gameId, pseudo, gameSource, commentTarget, contextTrickNum, contextCardIndex, currentTrickNum, fetchComments, onClearContext]);

  const handleDelete = useCallback(async (commentId: number) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/comments/${commentId}?pseudo=${encodeURIComponent(pseudo)}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        setComments(prev => prev.filter(c => c.id !== commentId));
      }
    } catch { /* ignore */ }
  }, [pseudo]);

  const relevantComments = useMemo(() => {
    if (currentTrickNum !== undefined) {
      return comments.filter(c =>
        c.target_type === 'game' ||
        (c.target_type === 'trick' && c.target_trick_num === currentTrickNum) ||
        (c.target_type === 'card' && c.target_trick_num === currentTrickNum) ||
        (c.target_type === 'bid'),
      );
    }
    return comments;
  }, [comments, currentTrickNum]);

  const contextLabel = commentContext?.label ?? (
    commentTarget === 'game' ? 'Général' :
    commentTarget === 'trick' ? `Pli ${contextTrickNum ?? currentTrickNum ?? '?'}` :
    commentTarget === 'card' ? `Carte` :
    commentTarget === 'bid' ? `Enchère` : ''
  );

  return (
    <div className="library-comments-panel">
      <div className="library-comments-header">
        <span className="library-comments-title">
          Commentaires ({relevantComments.length})
        </span>
      </div>

      <div className="library-comments-list">
        {loading && (
          <div className="library-comments-loading">Chargement…</div>
        )}

        {!loading && relevantComments.length === 0 && (
          <div className="library-comments-empty">Aucun commentaire</div>
        )}

        {relevantComments.map(comment => (
          <div key={comment.id} className="library-comment">
            <div className="library-comment-header">
              <span className="library-comment-author">{comment.author}</span>
              <span className="library-comment-date">
                {new Date(comment.created_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {targetLabel(comment) && (
                <span className="library-comment-target">{targetLabel(comment)}</span>
              )}
              {comment.author.toLowerCase() === pseudo.toLowerCase() && (
                <button
                  className="library-comment-delete"
                  onClick={() => handleDelete(comment.id)}
                  title="Supprimer"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="library-comment-text">{comment.text}</div>
          </div>
        ))}
      </div>

      {commentsEnabled && (
        <div className="library-comments-input">
          {commentContext && (
            <div className="library-comments-context-badge">
              <span>{contextLabel}</span>
              <button onClick={() => {
                setCommentTarget('game');
                setContextTrickNum(undefined);
                setContextCardIndex(undefined);
                onClearContext?.();
              }}>✕</button>
            </div>
          )}
          {!commentContext && (
            <div className="library-comments-target-selector">
              <button
                className={`library-tag ${commentTarget === 'game' ? 'active' : ''}`}
                onClick={() => setCommentTarget('game')}
              >
                Général
              </button>
              {currentTrickNum !== undefined && (
                <button
                  className={`library-tag ${commentTarget === 'trick' ? 'active' : ''}`}
                  onClick={() => { setCommentTarget('trick'); setContextTrickNum(currentTrickNum); }}
                >
                  Pli {currentTrickNum}
                </button>
              )}
            </div>
          )}
          <div className="library-comments-input-row">
            <input
              type="text"
              className="library-search-input"
              placeholder="Ajouter un commentaire…"
              value={newCommentText}
              onChange={e => setNewCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            />
            <button
              className="library-modal-btn primary"
              onClick={handleSubmit}
              disabled={!newCommentText.trim() || submitting}
              style={{ padding: '7px 12px', fontSize: '0.8rem' }}
            >
              Envoyer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
